/*
 * EventsPage — public /events with filters + rich cards (Iter 78)
 * =================================================================
 * Filter chips: All / First Team / Academy · Upcoming / Past ·
 *               All types / Δοκιμαστικά / Camp / Open Day / Tournament / ...
 * Cards link to /events/:id for full detail + RSVP.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import {
  Calendar as CalIcon, MapPin, Users, Euro, ChevronRight,
} from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BE = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  open_day: "Open Day",
  camp: "Camp",
  tournament: "Τουρνουά",
  webinar: "Webinar",
  social: "Κοινωνική",
  trial: "Δοκιμαστικά",
  other: "Εκδήλωση",
};

const TYPE_COLORS = {
  open_day: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  camp: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  tournament: "bg-[#F5A623]/15 text-[#F5A623] border-[#F5A623]/30",
  webinar: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  social: "bg-pink-500/15 text-pink-300 border-pink-500/30",
  trial: "bg-red-500/15 text-red-300 border-red-500/30",
  other: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
};

const SCOPE_LABEL = {
  first_team: "Πρώτη Ομάδα",
  academy: "Ακαδημία",
  both: "Όλοι",
};

const Chip = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
      active
        ? "bg-[#F5A623] text-black border-[#F5A623]"
        : "bg-transparent text-zinc-400 border-[#262626] hover:border-[#F5A623]/50 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const EventCard = ({ e }) => {
  const cover = e.cover_image_url
    ? (e.cover_image_url.startsWith("http") ? e.cover_image_url : `${BE}${e.cover_image_url}`)
    : null;
  const start = new Date(e.start_at);
  const capacityLeft =
    e.capacity != null ? e.capacity - (e.rsvp_count || 0) : null;
  const sold_out = capacityLeft != null && capacityLeft <= 0;
  const isPast = start.getTime() < Date.now();

  return (
    <Link
      to={`/events/${e.id}`}
      className="group relative bg-[#0a0a0a] border border-[#1e1e1e] rounded-xl overflow-hidden hover:border-[#F5A623]/50 hover:-translate-y-1 transition-all duration-300"
      data-testid={`event-card-${e.id}`}
    >
      {/* Cover with date badge overlay */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f]">
        {cover ? (
          <img
            loading="lazy"
            decoding="async"
            src={cover}
            alt={e.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <CalIcon size={48} className="text-[#F5A623]/25" />
          </div>
        )}

        {/* Date pill top-left */}
        <div className="absolute top-3 left-3 bg-black/85 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 text-center min-w-[52px]">
          <p className="text-[9px] uppercase tracking-widest text-[#F5A623] font-semibold">
            {start.toLocaleDateString("el-GR", { month: "short" })}
          </p>
          <p className="font-['Bebas_Neue'] text-2xl leading-none text-white">
            {start.getDate()}
          </p>
        </div>

        {/* Type + featured badges */}
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
          <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${TYPE_COLORS[e.event_type] || TYPE_COLORS.other}`}>
            {TYPE_LABELS[e.event_type] || "Εκδήλωση"}
          </span>
          {e.is_featured && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-[#F5A623] text-black font-semibold uppercase tracking-widest">
              Featured
            </span>
          )}
        </div>

        {/* Bottom gradient + scope */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black to-transparent pointer-events-none" />
        <div className="absolute bottom-3 left-3 flex items-center gap-2 text-[10px]">
          <span className="text-white/80 uppercase tracking-widest">
            {SCOPE_LABEL[e.team_scope] || SCOPE_LABEL.both}
          </span>
        </div>
        {isPast && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="text-white font-['Bebas_Neue'] text-2xl tracking-widest opacity-80">
              ΟΛΟΚΛΗΡΩΘΗΚΕ
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4">
        <h3 className="text-white font-semibold text-base mb-1.5 line-clamp-2 group-hover:text-[#F5A623] transition-colors">
          {e.title}
        </h3>
        {e.description && (
          <p className="text-zinc-500 text-xs line-clamp-2 mb-3">
            {e.description}
          </p>
        )}

        <div className="space-y-1 text-[11px] text-zinc-400">
          <div className="flex items-center gap-1.5">
            <CalIcon size={11} className="text-[#F5A623]" />
            {start.toLocaleString("el-GR", { dateStyle: "medium", timeStyle: "short" })}
          </div>
          {e.location && (
            <div className="flex items-center gap-1.5">
              <MapPin size={11} className="text-[#F5A623]" />
              <span className="truncate">{e.location}</span>
            </div>
          )}
          {e.capacity != null && (
            <div className="flex items-center gap-1.5">
              <Users size={11} className="text-[#F5A623]" />
              {e.rsvp_count || 0} / {e.capacity}
              {capacityLeft > 0 && capacityLeft <= 5 && !isPast && (
                <span className="text-amber-400">— μόλις {capacityLeft} απομένουν</span>
              )}
            </div>
          )}
        </div>

        {/* Footer row */}
        <div className="mt-4 pt-3 border-t border-[#1e1e1e] flex items-center justify-between">
          <div className="text-xs">
            {e.price_eur != null && e.price_eur > 0 ? (
              <span className="inline-flex items-center gap-1 text-white font-semibold">
                <Euro size={11} />
                {e.price_eur}
              </span>
            ) : (
              <span className="text-emerald-400 font-semibold text-xs">Δωρεάν</span>
            )}
          </div>
          <span className="inline-flex items-center gap-1 text-[11px] text-[#F5A623] font-semibold">
            {sold_out ? "Εξαντλήθηκε" : isPast ? "Λεπτομέρειες" : "Δήλωσε"}
            <ChevronRight size={12} className="group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
};

// -------------------------
// Main page
// -------------------------
const EventsPage = () => {
  const [searchParams] = useSearchParams();
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(
    ["first_team", "academy"].includes(searchParams.get("scope"))
      ? searchParams.get("scope") : "all"
  );
  const [when, setWhen] = useState(
    ["past", "all"].includes(searchParams.get("when"))
      ? searchParams.get("when") : "upcoming"
  );
  const [type, setType] = useState(
    Object.keys(TYPE_LABELS).includes(searchParams.get("type"))
      ? searchParams.get("type") : "all"
  );

  useEffect(() => {
    // Backend `upcoming_only=false` returns live + completed. We filter locally.
    axios
      .get(`${API}/marketing/events?upcoming_only=false`)
      .then((r) => setAllEvents(r.data || []))
      .catch(() => setAllEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const now = Date.now();
    return allEvents
      .filter((e) => {
        // Only show live-status events (draft/cancelled hidden — backend already
        // filters, but be safe).
        if (e.status && !["live", "completed"].includes(e.status)) return false;
        if (scope !== "all" && e.team_scope !== scope && e.team_scope !== "both") return false;
        if (type !== "all" && e.event_type !== type) return false;

        const startMs = new Date(e.start_at).getTime();
        if (when === "upcoming" && startMs < now) return false;
        if (when === "past" && startMs >= now) return false;

        return true;
      })
      .sort((a, b) => {
        const ta = new Date(a.start_at).getTime();
        const tb = new Date(b.start_at).getTime();
        return when === "past" ? tb - ta : ta - tb;
      });
  }, [allEvents, scope, when, type]);

  // Compute counts per filter for the chips
  const counts = useMemo(() => {
    const now = Date.now();
    const inScope = (e) => scope === "all" || e.team_scope === scope || e.team_scope === "both";
    return {
      all: allEvents.length,
      upcoming: allEvents.filter((e) => new Date(e.start_at).getTime() >= now && inScope(e)).length,
      past: allEvents.filter((e) => new Date(e.start_at).getTime() < now && inScope(e)).length,
      first_team: allEvents.filter((e) => e.team_scope === "first_team" || e.team_scope === "both").length,
      academy: allEvents.filter((e) => e.team_scope === "academy" || e.team_scope === "both").length,
    };
  }, [allEvents, scope]);

  return (
    <div className="min-h-screen bg-black" data-testid="events-page">
      <SEO
        title="Εκδηλώσεις · Λευτεριά FC"
        description="Δοκιμαστικά, open days, camps, τουρνουά — δες τι έρχεται και δήλωσε συμμετοχή."
      />

      {/* Hero */}
      <section className="relative pt-32 pb-8 px-6 overflow-hidden border-b border-[#1e1e1e]">
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(-45deg, #F5A623 0 2px, transparent 2px 24px)",
          }}
        />
        <div className="max-w-7xl mx-auto relative">
          <p className="text-[#F5A623] text-[10px] uppercase tracking-[0.3em] mb-2">
            Λευτεριά FC
          </p>
          <h1 className="font-['Bebas_Neue'] text-5xl md:text-7xl text-white tracking-tight leading-[0.95]">
            Εκδηλώσεις
          </h1>
          <p className="text-zinc-400 max-w-2xl mt-4 leading-relaxed text-sm md:text-base">
            Open days, camps, τουρνουά, δοκιμαστικά και εκδηλώσεις μας.
            Επίλεξε αυτό που σε ενδιαφέρει και κράτησε τη θέση σου.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="px-6 py-6 border-b border-[#1e1e1e] bg-[#050505] sticky top-16 z-30 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto space-y-3">
          <div className="flex flex-wrap items-center gap-2" data-testid="filter-scope">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Ομάδα</span>
            <Chip active={scope === "all"} onClick={() => setScope("all")} testId="filter-scope-all">
              Όλα ({counts.all})
            </Chip>
            <Chip active={scope === "first_team"} onClick={() => setScope("first_team")} testId="filter-scope-first-team">
              Πρώτη Ομάδα ({counts.first_team})
            </Chip>
            <Chip active={scope === "academy"} onClick={() => setScope("academy")} testId="filter-scope-academy">
              Ακαδημία ({counts.academy})
            </Chip>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="filter-when">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Χρόνος</span>
            <Chip active={when === "upcoming"} onClick={() => setWhen("upcoming")} testId="filter-when-upcoming">
              Προσεχείς ({counts.upcoming})
            </Chip>
            <Chip active={when === "past"} onClick={() => setWhen("past")} testId="filter-when-past">
              Παρελθόντες ({counts.past})
            </Chip>
            <Chip active={when === "all"} onClick={() => setWhen("all")} testId="filter-when-all">
              Όλες
            </Chip>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="filter-type">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Τύπος</span>
            <Chip active={type === "all"} onClick={() => setType("all")} testId="filter-type-all">Όλοι</Chip>
            {Object.entries(TYPE_LABELS).map(([k, l]) => (
              <Chip key={k} active={type === k} onClick={() => setType(k)} testId={`filter-type-${k}`}>
                {l}
              </Chip>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="px-6 py-10">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <p className="text-zinc-500 text-center py-20">Φόρτωση…</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-[#1e1e1e] rounded-xl" data-testid="events-empty">
              <CalIcon size={40} className="mx-auto text-zinc-700 mb-3" />
              <p className="text-zinc-400 text-sm">Δεν βρέθηκαν εκδηλώσεις για αυτό το φίλτρο.</p>
              <p className="text-zinc-600 text-xs mt-1">Δοκίμασε να αλλάξεις τα φίλτρα ή έλεγξε ξανά σύντομα.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map((e) => <EventCard key={e.id} e={e} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
